import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, GripVertical, Eye, EyeOff, Calendar, X, Image as ImageIcon, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import ImageUpload from '../../components/ImageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminBanners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    cta_text: 'Shop Now',
    cta_link: '/products',
    desktop_image: '',
    mobile_image: '',
    is_active: true,
    priority: 0,
    start_date: '',
    end_date: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/banners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBanners(response.data.banners || []);
    } catch (error) {
      toast.error('Failed to load banners');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingBanner(null);
    setFormData({
      title: '',
      subtitle: '',
      cta_text: 'Shop Now',
      cta_link: '/products',
      desktop_image: '',
      mobile_image: '',
      is_active: true,
      priority: banners.length,
      start_date: '',
      end_date: ''
    });
    setShowModal(true);
  };

  const openEditModal = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title || '',
      subtitle: banner.subtitle || '',
      cta_text: banner.cta_text || 'Shop Now',
      cta_link: banner.cta_link || '/products',
      desktop_image: banner.desktop_image || '',
      mobile_image: banner.mobile_image || '',
      is_active: banner.is_active !== false,
      priority: banner.priority || 0,
      start_date: banner.start_date || '',
      end_date: banner.end_date || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.desktop_image) {
      toast.error('Title and desktop image are required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      if (editingBanner) {
        await axios.put(`${API}/admin/banners/${editingBanner.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Banner updated');
      } else {
        await axios.post(`${API}/admin/banners`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Banner created');
      }
      
      setShowModal(false);
      loadBanners();
    } catch (error) {
      toast.error('Failed to save banner');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bannerId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/banners/${bannerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Banner deleted');
      setShowDeleteConfirm(null);
      loadBanners();
    } catch (error) {
      toast.error('Failed to delete banner');
    }
  };

  const toggleBannerStatus = async (banner) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/banners/${banner.id}`, {
        is_active: !banner.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadBanners();
      toast.success(`Banner ${!banner.is_active ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error('Failed to update banner');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-banners">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-light text-charcoal">Hero Banners</h1>
          <p className="text-charcoal-light text-sm mt-1">Manage homepage hero banners</p>
        </div>
        <button
          onClick={openAddModal}
          className="flex items-center gap-2 bg-gold text-white px-4 py-2 text-sm font-medium hover:bg-gold-light transition-colors"
          data-testid="add-banner-btn"
        >
          <Plus className="w-4 h-4" />
          Add Banner
        </button>
      </div>

      {/* Banners Grid */}
      <div className="grid gap-6">
        {banners.length > 0 ? (
          banners.map((banner, index) => (
            <div
              key={banner.id}
              className={`bg-white border ${banner.is_active ? 'border-charcoal/10' : 'border-red-200 bg-red-50/30'} overflow-hidden`}
            >
              <div className="flex flex-col lg:flex-row">
                {/* Banner Preview */}
                <div className="lg:w-1/3 relative">
                  <img
                    src={banner.desktop_image}
                    alt={banner.title}
                    className="w-full h-48 lg:h-full object-cover"
                  />
                  {!banner.is_active && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="bg-red-500 text-white px-3 py-1 text-sm font-medium">INACTIVE</span>
                    </div>
                  )}
                  <span className="absolute top-2 left-2 bg-charcoal/80 text-white px-2 py-1 text-xs">
                    Priority: {banner.priority + 1}
                  </span>
                </div>
                
                {/* Banner Info */}
                <div className="flex-1 p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-heading text-xl font-light text-charcoal">{banner.title}</h3>
                      {banner.subtitle && (
                        <p className="text-charcoal-light text-sm mt-1">{banner.subtitle}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleBannerStatus(banner)}
                        className={`p-2 ${banner.is_active ? 'text-green-600 hover:bg-green-50' : 'text-red-600 hover:bg-red-50'} transition-colors`}
                        title={banner.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {banner.is_active ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                      </button>
                      <button
                        onClick={() => openEditModal(banner)}
                        className="p-2 text-charcoal-light hover:text-gold hover:bg-beige transition-colors"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(banner.id)}
                        className="p-2 text-charcoal-light hover:text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-charcoal-light">CTA Button:</span>
                      <p className="text-charcoal font-medium">{banner.cta_text}</p>
                    </div>
                    <div>
                      <span className="text-charcoal-light">Link:</span>
                      <p className="text-charcoal font-medium truncate">{banner.cta_link}</p>
                    </div>
                    {(banner.start_date || banner.end_date) && (
                      <div className="col-span-2">
                        <span className="text-charcoal-light">Schedule:</span>
                        <p className="text-charcoal">
                          {banner.start_date && `From: ${new Date(banner.start_date).toLocaleDateString()}`}
                          {banner.start_date && banner.end_date && ' - '}
                          {banner.end_date && `To: ${new Date(banner.end_date).toLocaleDateString()}`}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {banner.mobile_image && (
                    <div className="mt-4 flex items-center gap-2 text-sm text-charcoal-light">
                      <ImageIcon className="w-4 h-4" />
                      <span>Mobile image configured</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white border border-charcoal/10 p-12 text-center">
            <ImageIcon className="w-12 h-12 mx-auto text-charcoal-light mb-4" />
            <h3 className="font-medium text-charcoal">No banners yet</h3>
            <p className="text-charcoal-light text-sm mt-1">Create your first hero banner</p>
            <button
              onClick={openAddModal}
              className="mt-4 bg-gold text-white px-4 py-2 text-sm font-medium hover:bg-gold-light transition-colors"
            >
              Add Banner
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/50">
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-charcoal/10 p-6 flex items-center justify-between">
              <h2 className="font-heading text-xl font-light text-charcoal">
                {editingBanner ? 'Edit Banner' : 'Add Banner'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-charcoal-light hover:text-charcoal">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                  Banner Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full h-11 px-4 border border-charcoal/10 focus:border-gold outline-none"
                  placeholder="Summer Collection 2025"
                />
              </div>
              
              {/* Subtitle */}
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                  Subtitle
                </label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  className="w-full h-11 px-4 border border-charcoal/10 focus:border-gold outline-none"
                  placeholder="Up to 50% off on selected items"
                />
              </div>
              
              {/* CTA */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                    CTA Button Text
                  </label>
                  <input
                    type="text"
                    value={formData.cta_text}
                    onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                    className="w-full h-11 px-4 border border-charcoal/10 focus:border-gold outline-none"
                    placeholder="Shop Now"
                  />
                </div>
                <div>
                  <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                    CTA Link
                  </label>
                  <input
                    type="text"
                    value={formData.cta_link}
                    onChange={(e) => setFormData({ ...formData, cta_link: e.target.value })}
                    className="w-full h-11 px-4 border border-charcoal/10 focus:border-gold outline-none"
                    placeholder="/products"
                  />
                </div>
              </div>
              
              {/* Desktop Image */}
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                  Desktop Image * (1920x600 recommended)
                </label>
                <ImageUpload
                  onImageUploaded={(url) => setFormData({ ...formData, desktop_image: url })}
                  currentImage={formData.desktop_image}
                />
              </div>
              
              {/* Mobile Image */}
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                  Mobile Image (750x800 recommended)
                </label>
                <ImageUpload
                  onImageUploaded={(url) => setFormData({ ...formData, mobile_image: url })}
                  currentImage={formData.mobile_image}
                />
              </div>
              
              {/* Scheduling */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                    Start Date (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full h-11 px-4 border border-charcoal/10 focus:border-gold outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                    End Date (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full h-11 px-4 border border-charcoal/10 focus:border-gold outline-none"
                  />
                </div>
              </div>
              
              {/* Priority & Active */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                    Priority (Lower = First)
                  </label>
                  <input
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                    className="w-full h-11 px-4 border border-charcoal/10 focus:border-gold outline-none"
                    min="0"
                  />
                </div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-5 h-5 accent-gold"
                    />
                    <span className="text-sm text-charcoal">Active</span>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="sticky bottom-0 bg-white border-t border-charcoal/10 p-6 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 border border-charcoal/20 text-charcoal text-sm font-medium hover:bg-beige transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-gold text-white text-sm font-medium hover:bg-gold-light disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving...' : editingBanner ? 'Update Banner' : 'Create Banner'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/50">
          <div className="bg-white p-6 max-w-sm w-full">
            <h3 className="font-heading text-lg font-light text-charcoal mb-2">Delete Banner?</h3>
            <p className="text-charcoal-light text-sm mb-6">This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 border border-charcoal/20 text-charcoal text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBanners;
